<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              {{ fromMyConsole ? "我的主控台" : "獎勵應用中心" }}
            </b-breadcrumb-item>
            <b-breadcrumb-item active>{{ title }}</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <!-- 第一層：標題 + 操作按鈕 -->
      <div class="row d-flex mb-2 justify-content-between align-items-center">
        <h4 class="col-12 col-xl-auto mb-2 mb-xl-0 font-weight-bold">
          {{ title }}
        </h4>
        <div class="col-12 col-xl-auto d-flex flex-wrap">
          <b-button
            v-if="$permissions.has(consts.REDEEM_CODE_USED_LIST_VIEW)"
            class="mb-2 mb-xl-0 mr-2"
            variant="link"
            :to="{
              name: 'BelongedRedeemCodeList',
            }"
          >
            <i class="fa fa-folder-open"></i>優惠券使用紀錄
          </b-button>
          <b-button
            v-if="canCreateRedeemCodeBundle"
            class="mb-2 mb-xl-0 mr-2"
            variant="primary"
            @click="handleCreate"
          >
            <i class="fa fa-plus"></i>新增優惠券
          </b-button>
        </div>
      </div>

      <!-- 第二層：狀態篩選 + 進階搜尋按鈕 -->
      <div class="d-flex flex-wrap align-items-center mb-2">
        <b-form-select
          v-model="query.is_valid"
          :options="validityStatusOptions"
          class="mr-2"
          style="max-width: 160px; height: 35px; padding-top: 4px; padding-bottom: 4px;"
          @change="handlePageReset"
        ></b-form-select>
        <b-form-select
          v-model="query.status"
          :options="statusOptions"
          class="mr-3"
          style="max-width: 160px; height: 35px; padding-top: 4px; padding-bottom: 4px;"
          @change="handlePageReset"
        ></b-form-select>
        <b-button v-b-toggle.collapse-advanced-search variant="outline-info" style="height: 34px;">
          <span class="mdi mdi-magnify mt-1 mr-1"></span>進階搜尋
        </b-button>
      </div>

      <!-- 第三層：進階搜尋折疊區塊 -->
      <b-collapse id="collapse-advanced-search" v-model="isAdvancedSearchVisible" class="mb-2">
        <b-card>
          <b-form>
            <div class="d-flex flex-column flex-xl-row flex-wrap">
              <b-form-group
                label="分店"
                label-cols-sm="3"
                label-cols-lg="2"
                content-cols-sm
                content-cols-lg="7"
                style="width: 50%"
              >
                <AppSingleSelect
                  v-model="selectedBranch"
                  :options="branchOptions"
                  :disabled="disabled_branch_select"
                  @input="branchChange"
                ></AppSingleSelect>
              </b-form-group>

              <b-form-group
                label="通路"
                label-cols-sm="3"
                label-cols-lg="2"
                content-cols-sm
                content-cols-lg="7"
                style="width: 50%"
              >
                <b-form-select
                  v-model="query.merchant_id"
                  :options="merchants"
                  @change="handlePageReset"
                ></b-form-select>
              </b-form-group>

              <b-form-group
                label="領取方式"
                label-cols-sm="3"
                label-cols-lg="2"
                content-cols-sm
                content-cols-lg="7"
                style="width: 50%"
              >
                <b-form-select
                  v-model="query.take_type"
                  :options="takeTypes"
                  @change="handlePageReset"
                ></b-form-select>
              </b-form-group>
            </div>
            <div class="d-flex">
              <b-button
                variant="primary"
                class="mb-2 mb-xl-0 mr-2"
                @click="reset"
              ><i class="mdi mdi-restart"></i> 重置條件</b-button>
              <b-button
                variant="primary"
                class="mb-2 mb-xl-0"
                @click="handlePageReset"
              >
                <i class="fa fa-search"></i> 搜尋
              </b-button>
            </div>
          </b-form>
        </b-card>
      </b-collapse>

      <!-- 第四層：關鍵字搜尋框 -->
      <b-card class="mb-2">
        <b-input-group>
          <b-form-input
            v-model="query.keyword"
            v-on:keyup.enter="handlePageReset"
            placeholder="請輸入優惠券名稱"
          ></b-form-input>
          <b-input-group-append>
            <b-button @click="handlePageReset">
              <i class="fa fa-search"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-card>

      <b-table
        striped
        hover
        responsive
        :items="redeemCodeBundles"
        :fields="fields"
        :busy="isLoading"
      >
        <template #cell(img_url)="data">
          <b-img-lazy
            v-if="data.item.img_url"
            class="rounded-0"
            style="width: 100px; height: 50px"
            :src="data.item.img_url"
            alt="圖片"
          />
        </template>
        <template #cell(start_at_and_end_at)="data">
          <div class="mb-1">{{ formatDate(data.item.start_at) }} ~</div>
          <div>{{ formatDate(data.item.end_at) }}</div>
        </template>
        <template #cell(branch_id)="data">
          <div>{{ getBranchName(data.item.branch_id) }}</div>
        </template>
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(actions)="data">
          <span
            :id="`show-qrcode-wrapper-${data.item.id}`"
            class="d-inline-block"
            tabindex="0"
          >
            <b-button
              :id="`show-qrcode-${data.item.id}`"
              class="ml-2"
              variant="inverse-dark"
              @click="showAssignUrlModal(data.item)"
              :disabled="
                data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.DIRECT &&
                data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.INPUT_CODE &&
                data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.URL_TAKE &&
                data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.POINT
              "
            >
              <i class="fa fa-link ml-2"></i>
            </b-button>
          </span>
          <b-tooltip
            v-if="
              data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.DIRECT &&
              data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.INPUT_CODE &&
              data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.URL_TAKE &&
              data.item.take_type !== consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.POINT
            "
            :target="`show-qrcode-wrapper-${data.item.id}`"
            triggers="hover focus"
            title="領取方式不支援網址領取"
          />
          <b-modal
            :id="`assign-url-modal-${data.item.id}`"
            footer-class="modal-button-group d-flex w-100 align-items-center"
          >
            <template #modal-header="{ close }">
              <div
                class="d-flex w-100 align-items-center justify-content-between"
              >
                <h5 class="m-0">{{ data.item.name }}</h5>
                <b-button
                  size="sm"
                  variant="link"
                  class="text-dark p-0"
                  @click="close()"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                    style="height: 20px"
                  >
                    <path
                      d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z"
                    />
                  </svg>
                </b-button>
              </div>
            </template>
            <div class="d-flex flex-column align-items-center">
              <h5 class="mb-2">領取網址</h5>
              <b-textarea
                id="assign-url"
                v-b-tooltip.click="'已複製'"
                v-model="assignUrl"
                @click="copyAssignUrl"
                class="mt-2"
              ></b-textarea>
              <div>
                <vue-qr :text="assignUrl" ref="assignQRcode"></vue-qr>
              </div>
            </div>
            <template #modal-footer>
              <div
                class="modal-button-group d-flex w-100 align-items-center"
                style="position: relative; min-height: 42px"
              >
                <div class="flex-grow-1 d-flex justify-content-center">
                  <b-button
                    variant="primary"
                    @click="downloadQRCode(assignUrl, data.item.name)"
                  >
                    下載QRCode
                  </b-button>
                </div>
                <b-button
                  variant="danger"
                  style="position: absolute; right: 0"
                  @click="$bvModal.hide(`assign-url-modal-${data.item.id}`)"
                >
                  關閉
                </b-button>
              </div>
            </template>
          </b-modal>

          <b-dropdown
            class="ml-2"
            v-if="
              $permissions.hasAny([
                consts.REDEEM_CODE_LIST_CREATE,
                consts.REDEEM_CODE_LIST_MODIFY,
              ]) || fromMyConsole
            "
            variant="secondary"
          >
            <template slot="button-content">
              <i class="mdi mdi-menu"></i>
            </template>
            <b-dropdown-item
              v-if="
                $permissions.has(consts.REDEEM_CODE_LIST_MODIFY) ||
                fromMyConsole
              "
              :to="{
                name: fromMyConsole
                  ? 'MyConsoleRedeemCodeBundleRedeemCodes'
                  : 'RedeemCodeBundleRedeemCodes',
                params: {
                  redeemCodeBundleId: data.item.id,
                },
              }"
            >
              使用紀錄
            </b-dropdown-item>
            <b-dropdown-item
              v-if="
                $permissions.has(consts.REDEEM_CODE_LIST_MODIFY) &&
                data.item.redeemcode_type !==
                  consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.PACKAGE
              "
              @click="modify(data.item)"
            >
              數量設定
            </b-dropdown-item>
            <b-dropdown-item
              v-if="
                $permissions.has(consts.REDEEM_CODE_LIST_CREATE) ||
                fromMyConsole
              "
              :to="{
                name: fromMyConsole
                  ? 'MyConsoleRedeemCodeBundleCreate'
                  : 'RedeemCodeBundleCreate',
                query: {
                  copyFromId: data.item.id,
                },
              }"
            >
              複製
            </b-dropdown-item>
          </b-dropdown>

          <b-button
            class="ml-2"
            variant="inverse-primary"
            :to="{
              name: fromMyConsole
                ? 'MyConsoleRedeemCodeBundleDetail'
                : 'RedeemCodeBundleDetail',
              params: {
                redeemCodeBundleId: data.item.id,
              },
            }"
          >
            <span class="mdi mdi-eye"></span> 查看
          </b-button>

          <b-button
            v-if="canModifyRedeemCodeBundle"
            class="ml-2"
            variant="inverse-warning"
            :to="{
              name: fromMyConsole
                ? 'MyConsoleRedeemCodeBundleEdit'
                : 'RedeemCodeBundleEdit',
              params: {
                redeemCodeBundleId: data.item.id,
              },
            }"
          >
            <span class="mdi mdi-lead-pencil"></span> 編輯
          </b-button>
          <span :id="`soft-delete-button-${data.item.id}`">
            <b-button
              v-if="
              $permissions.has(consts.REDEEM_CODE_LIST_SOFT_DELETE) || fromMyConsole
            "
              class="ml-2"
              variant="inverse-danger"
              :disabled="false === redeemCodeBundleDataModels[data.item.id].isDeletable"
              @click="handleDelete(data.item, true)"
            >
              <span class="mdi mdi-minus-circle"></span> 刪除
            </b-button>
          </span>
          <b-tooltip
            v-if="false === redeemCodeBundleDataModels[data.item.id].isDeletable"
            :target="`soft-delete-button-${data.item.id}`"
            triggers="hover"
            title="發布狀態為草稿或關閉時，才能刪除"
          />
          <span :id="`hard-delete-button-${data.item.id}`">
            <b-button
              v-if="
                $permissions.has(consts.REDEEM_CODE_LIST_DELETE) || fromMyConsole
              "
              class="ml-2"
              variant="inverse-danger"
              @click="handleDelete(data.item, false)"
              :disabled="false === redeemCodeBundleDataModels[data.item.id].isDeletable"
            >
              <span class="mdi mdi-delete"></span> 永久刪除
            </b-button>
          </span>
          <b-tooltip
            v-if="false === redeemCodeBundleDataModels[data.item.id].isDeletable"
            :target="`hard-delete-button-${data.item.id}`"
            triggers="hover"
            title="發布狀態為草稿或關閉時，才能刪除"
          />
        </template>
        <template #cell(is_valid)="data">
          <b-badge
            :variant="data.item.is_valid ? 'success' : 'danger'"
          >
            {{ data.item.is_valid ? '生效中' : '已失效' }}
          </b-badge>
        </template>
        <template #cell(status)="data">
          <b-badge v-if="data.item.status == consts.PUBLISH_STATUS.DRAFT" variant="warning">
            草稿
          </b-badge>
          <b-badge v-if="data.item.status == consts.PUBLISH_STATUS.PUBLISHED" variant="success">
            發布
          </b-badge>
          <b-badge
            v-if="data.item.status == consts.PUBLISH_STATUS.CLOSED"
            variant="danger"
          >
            關閉
          </b-badge>
        </template>
        <template #cell(usage_status)="data">
          <div v-html="formatUsageStatus(data.item)"></div>
        </template>
        <template #cell(take_usage_type)="data">
          <div v-html="formatTakeUsageType(data.item)"></div>
        </template>
      </b-table>
      <CustomPagination
        :currentPage="query.page"
        :totalRows="total"
        :perPage="query.per_page"
        :perPageOptions="perPageOption"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>

    <b-modal
      id="modify-modal"
      footer-class="d-flex justify-content-center"
      centered
    >
      <template #modal-header>
        <h5 class="mb-2" v-html="selectedBundle.title">數量調整</h5>
      </template>
      <b-form-group
        label-cols="12"
        label-cols-lg="3"
        label-size="sm"
        label="序號"
        label-for="type"
      >
        <b-form-radio-group
          v-model="selectedBundle.redeemcode_type"
          :options="redeemCodeTypes"
          disabled
        >
        </b-form-radio-group>
      </b-form-group>
      <!-- 固定序號設定 start -->
      <b-form-group
        label-cols="12"
        label-cols-lg="3"
        label-size="sm"
        label="固定序號"
        label-for="name"
        v-if="
          selectedBundle.redeemcode_type ==
          consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.FIXED
        "
      >
        <b-form-input
          class="mb-2 mr-sm-2 mb-sm-0"
          type="text"
          v-model="selectedBundle.static_redeem_code"
          disabled
        ></b-form-input>
      </b-form-group>

      <!-- 隨機序號設定 start -->
      <b-form-group
        label-cols="12"
        label-cols-lg="3"
        label-size="sm"
        label="隨機序號長度"
        label-for="name"
        v-if="
          selectedBundle.redeemcode_type ==
          consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.RANDOM
        "
      >
        <b-form-input
          class="mb-2 mr-sm-2 mb-sm-0"
          type="text"
          v-model="selectedBundle.random_redeem_code_length"
          disabled
        ></b-form-input>
      </b-form-group>
      <!-- 隨機序號設定 end -->

      <!-- 目前數量上限 start -->
      <b-form-group
        label-cols="12"
        label-cols-lg="3"
        label-size="sm"
        label="目前數量上限"
        label-for="name"
        v-if="
          selectedBundle.redeemcode_type !==
          consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.PACKAGE
        "
      >
        <b-form-input
          class="mb-2 mr-sm-2 mb-sm-0"
          type="text"
          v-model="selectedBundle.limit"
          placeholder="不填寫表示無限"
          disabled
        ></b-form-input>
      </b-form-group>
      <!-- 目前數量上限 end -->

      <!-- 調整數量 start -->
      <b-form-group
        label-cols="12"
        label-cols-lg="3"
        label-size="sm"
        label-for="name"
        v-if="
          selectedBundle.redeemcode_type !==
          consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.PACKAGE
        "
      >
        <template #label>
          <span
            v-b-tooltip.hover="'請先編輯數量上限，才能調整數量'"
            v-b-tooltip.html.hover.bottom="{ customClass: 'text-left' }"
          >
            調整數量
             <i
               class="mdi mdi-help-circle-outline ml-1"
               style="color: #6c757d;"
             ></i>
          </span>
        </template>
        <div class="d-flex align-items-center">
          <!-- Radio Button Group -->
          <b-form-radio-group
            :disabled="selectedBundle.limit === null"
            v-model.number="selectedBundle.action"
            class="col-6 d-flex"
          >
            <b-form-radio :value="1" class="mr-2">增加</b-form-radio>
            <b-form-radio :value="0">減少</b-form-radio>
          </b-form-radio-group>

          <!-- 輸入框 -->
          <b-form-input
            type="number"
            :disabled="selectedBundle.limit === null"
            v-model="selectedBundle.quantity"
            class="col-6 d-flex"
          ></b-form-input>
        </div>
      </b-form-group>
      <!-- 調整數量 end -->

      <!-- 調整後數量上限 start -->
      <b-form-group
        label-cols="12"
        label-cols-lg="3"
        label-size="sm"
        label="調整後數量上限"
        label-for="name"
        v-if="
          selectedBundle.redeemcode_type !==
          consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.PACKAGE
        "
      >
        <b-form-input
          class="mb-2 mr-sm-2 mb-sm-0"
          type="text"
          :value="resultQuantity"
          disabled
        ></b-form-input>
      </b-form-group>
      <!-- 調整後數量上限 end -->
      <template #modal-footer>
        <div class="d-flex justify-content-end w-100">
          <b-button
            size="md"
            variant="danger"
            @click="$bvModal.hide('modify-modal')"
            class="mr-2"
            >取消</b-button
          >
          <b-button :disabled="selectedBundle.limit === null || isLoading" size="md" variant="success" @click="submit()">
            確認送出
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import merchantApi from "../../../apis/merchant";
import redeemCodeBundleApi from "../../../apis/redeemCodeBundle";
import * as consts from "@/consts";
import { format } from "date-fns";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import { paginationMixin } from "@/mixins/pagination";
import redeemCodeBundleFiltersMixin from "@/mixins/redeemCodeBundleFilters";
import RedeemCodeBundle from "@/models/RedeemCodeBundle.model"
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import PermissionChecker from "@/utils/PermissionChecker";
import VueQr from "vue-qr";
import {REDEEM_CODE_BUNDLE_TAKE_TYPES} from "@/consts";

export default {
  components: {
    AppSingleSelect,
    CustomPagination,
    VueQr,
  },
  mixins: [paginationMixin, redeemCodeBundleFiltersMixin],
  data() {
    const fromMyConsole = this.$route.name === "MyConsoleRedeemCodeBundleList";

    const baseQuery = {
      page: 1,
      per_page: 10,
      status: '*',
      merchant_id: null,
      create_type: "0",
      keyword: "",
      take_type: "",
      branch_id: null,
      is_valid: null,
    };
    return {
      consts,
      isLoading: false,
      isAdvancedSearchVisible: false,
      search: "",
      merchants: [{ value: null, text: "請選擇" }],
      createTypes: [
        { value: null, text: "全部" },
        { value: "0", text: "組織優惠券" },
        { value: "1", text: "員工優惠券" },
      ],
      validityStatusOptions: [
        { value: null, text: '選擇生效狀態' },
        { value: true, text: '生效中' },
        { value: false, text: '已失效' }
      ],
      redeemCodeBundles: [],
      fields: [
        {
          key: "img_url",
          label: "圖片",
        },
        {
          key: "name",
          label: "獎項名稱",
          formatter: (value) =>
            value.length > 20 ? value.substring(0, 20) + "..." : value,
        },
        {
          key: "is_valid",
          label: "生效狀態",
        },
        {
          key: "status",
          label: "發布狀態",
        },
        {
          key: "branch_id",
          label: "分店",
        },
        {
          key: "usage_status",
          label: "狀態",
        },
        {
          key: "type",
          label: "獎項種類",
          formatter: (value) => {
            return consts.REDEEM_CODE_BUNDLE_TYPE_TRANSLATE[value];
          },
        },
        {
          key: "start_at_and_end_at",
          label: "開始/結束日",
        },
        {
          key: "redeem_codes_amount",
          label: "總數",
          formatter: (value) => {
            if (value > 9999999) {
              value = 9999999;
              return (
                value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "+"
              );
            } else {
              return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
          },
        },
        {
          key: "apply_at_amount",
          label: "已領取",
          formatter: (value) => {
            if (value > 9999999) {
              value = 9999999;
              return (
                value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "+"
              );
            } else {
              return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
          },
        },
        {
          key: "redeem_at_amount",
          label: "已兌換",
          formatter: (value) => {
            if (value > 9999999) {
              value = 9999999;
              return (
                value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "+"
              );
            } else {
              return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
          },
        },
        {
          key: "take_usage_type",
          label: "領取/使用方式",
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      perPage: 15,
      currentPage: 1,
      total: 0,
      fromMyConsole,
      title: fromMyConsole ? "我的優惠券" : "優惠券管理",
      assignUrl: "",
      initialized: false,
      perPageOption: [
        { text: 10, value: 10 },
        { text: 20, value: 20 },
        { text: 50, value: 50 },
        { text: 100, value: 100 },
      ],
      query: JSON.parse(JSON.stringify(baseQuery)),
      initQuery: JSON.parse(JSON.stringify(baseQuery)),
      selectedBundle: {
        id: 0,
        merchant_id: 0,
        name: "",
        title: "",
        redeemcode_type: 0,
        quantity: 0,
        limit: 0,
        action: 1, // 0:減少,1:增加
        static_redeem_code: "",
        random_redeem_code_length: "",
      },
      redeemCodeTypes: [
        {
          text: "固定序號",
          value: consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.FIXED,
        },
        {
          text: "隨機序號",
          value: consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.RANDOM,
        },
        {
          text: "不使用序號",
          value: consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.NONE,
        },
      ],
      usageTypes: [
        {
          text: "需立即使用",
          value: consts.REDEEM_CODE_BUNDLE_USAGE_TYPES.IMMEDIATE,
        },
        {
          text: "無須立即使用",
          value: consts.REDEEM_CODE_BUNDLE_USAGE_TYPES.NOT_IMMEDIATE,
        },
      ],
      disabled_branch_select: true,
    };
  },
  computed: {
    REDEEM_CODE_BUNDLE_TAKE_TYPES() {
      return REDEEM_CODE_BUNDLE_TAKE_TYPES
    },
    ...mapState("general", {
      organization: (state) => state.organization,
      defaultMerchantType: (state) => state.defaultMerchantType,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    resultQuantity() {
      const limit = Number(this.selectedBundle.limit);
      const quantity = Number(this.selectedBundle.quantity);

      return this.selectedBundle.action === 1
        ? limit + quantity
        : limit - quantity;
    },
    statusOptions() {
      return [
        { text: "選擇發布狀態", value: '*' },
        { text: "發布", value: consts.PUBLISH_STATUS.PUBLISHED },
        { text: "草稿", value: consts.PUBLISH_STATUS.DRAFT },
        { text: "關閉", value: consts.PUBLISH_STATUS.CLOSED },
      ]
    },
    canCreateRedeemCodeBundle() {
      return this.fromMyConsole || this.checkAnyPermission([
        consts.REDEEM_CODE_LIST_CREATE,
        consts.REDEEM_CODE_BY_ROLE_CREATE,
      ]);
    },
    canModifyRedeemCodeBundle() {
      return this.fromMyConsole || this.checkAnyPermission([
        consts.REDEEM_CODE_LIST_MODIFY,
        consts.REDEEM_CODE_BY_ROLE_MODIFY,
      ]);
    },
    redeemCodeBundleDataModels() {
      return this.redeemCodeBundles.reduce((acc, bundle) => {
        return { ...acc, [bundle.id]: new RedeemCodeBundle(bundle) }
      }, {})
    },
  },
  async mounted() {
    // 有權限的話，可以編輯下拉
    if (this.checkPermission([consts.REDEEM_CODE_LIST_BRANCH])) {
      this.disabled_branch_select = false;
    }

    await Promise.all([this.fetchMerchants(), this.fetchRedeemCodeBundles()]);
    this.initialized = true;
    this.checkAndExpandAdvancedSearch();
  },
  async created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      const defaultQuery = { merchant_id: null };
      this.query = { ...defaultQuery, ...to.query };
      this.fetchRedeemCodeBundles();
      this.checkAndExpandAdvancedSearch();
    },
  },
  methods: {
    checkAndExpandAdvancedSearch() {
      // 當有 branch_id, merchant_id 或 take_type 參數時，自動展開進階搜尋
      const hasAdvancedParams = this.$route.query.branch_id || this.$route.query.merchant_id || this.$route.query.take_type;

      if (hasAdvancedParams && !this.isAdvancedSearchVisible) {
        this.$nextTick(() => {
          this.isAdvancedSearchVisible = true;
        });
      }
    },
    branchChange() {
      this.query.branch_id = this.selectedBranch?.value ?? null;
      this.handlePageReset();
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    checkAnyPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.checkAny(permissions);
    },
    async fetchMerchants() {
      const { data } = await merchantApi.list();

      this.merchants = [
        { value: null, text: "請選擇" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: `${merchant.name} (${merchant.type_name})`,
          };
        }),
      ];

      if (this.$route.query.merchant_id) {
        this.query.merchant_id = this.$route.query.merchant_id;

        var hasMerchant = this.merchants.find((item) => {
          return item.value === this.query.merchant_id;
        });

        if (!hasMerchant) {
          this.$swal.fire({
            type: "warning",
            title: "查無此通路",
            confirmButtonText: "確定",
            confirmButtonColor: "#d33",
            reverseButtons: true,
          });
          return;
        }
      } else {
        const defaultMerchant =
          data.data.find(
            (merchant) => merchant.type === this.defaultMerchantType
          ) ??
          data.data.find(
            (merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE
          );

        if (defaultMerchant) {
          this.query.merchant_id = defaultMerchant.id;
        } else {
          this.query.merchant_id = data.data[0].id;
        }
      }
    },
    async fetchRedeemCodeBundles() {
      try {
        if (this.query.branch_id === "null") {
          this.query.branch_id = null;
        }

        // 避免使用者沒有跟選任何分店
        // 當沒有 branch_id 但是沒有無分店權限則自動帶入預設分店
        if (
          this.query.branch_id === null &&
          !this.checkPermission([consts.REDEEM_CODE_LIST_NO_BRANCH])
        ) {
          this.query.branch_id = this.currentBranch.id;
          this.selectedBranch = this.branchOptions.find(
            (option) => option.value === this.currentBranch.id
          );
        }

        this.isLoading = true;
        const params = {
          fromMyConsole: this.fromMyConsole,
          ...this.query,
        };
        if (this.query.status === "*") {
          delete params.status
        }
        if (params.is_valid === null) {
          delete params.is_valid;
        }
        const { data } = await redeemCodeBundleApi.getRedeemCodeBundles(params);
        this.total = data.meta.total;
        this.perPage = data.meta.per_page;

        this.redeemCodeBundles = data.data.map((item) => ({
          ...item,
          take_usage_type: {
            take_type: item.take_type,
            usage_type: item.usage_type,
          },
        }));
      } catch (error) {
        console.log("");
      }
      this.isLoading = false;
    },
    showAssignUrlModal(redeemCodeBundle) {
      if (redeemCodeBundle.take_type === consts.REDEEM_CODE_BUNDLE_TAKE_TYPES.POINT) {
        this.assignUrl = `https://liff.line.me/${this.organization.liff_id}/point/redeem_coupon/${redeemCodeBundle.id}`;
      } else {
        this.assignUrl = `https://liff.line.me/${this.organization.liff_id}/coupon/auto_apply/${redeemCodeBundle.id}`;
      }
      this.$bvModal.show(`assign-url-modal-${redeemCodeBundle.id}`);
    },
    copyAssignUrl() {
      const inputText = document.querySelector("#assign-url");
      inputText.select();
      document.execCommand("copy");
    },
    downloadQRCode(url, filename) {
      const ref = this.$refs.assignQRcode;
      const base64QRCodeImage = ref?.$el?.src;
      if (base64QRCodeImage) {
        const temporaryDownloadLink = document.createElement("a");
        temporaryDownloadLink.href = base64QRCodeImage;
        temporaryDownloadLink.download = filename
          ? `${filename}.png`
          : "qrcode.png";
        temporaryDownloadLink.style.display = "none";
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
        document.body.removeChild(temporaryDownloadLink);
      }
    },
    async handleDelete(redeemCodeBundle, softDelete) {
      this.$swal
        .fire({
          type: "warning",
          title: "你確定要刪除嗎？",
          html: `
              <div class="my-3">
                <div>優惠券名稱：${redeemCodeBundle.name}</div>
                <div>所有優惠券明細(包含已發送)皆會一併刪除。</div>
              </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            try {
              this.isLoading = true;
              await redeemCodeBundleApi.deleteRedeemCodeBundle(
                redeemCodeBundle.id,
                {
                  'soft_delete': softDelete
                }
              );
              await this.fetchRedeemCodeBundles();
              this.$swal.fire({
                type: "success",
                text: `成功刪除 ${redeemCodeBundle.name}`,
              });
            } catch (error) {
              console.log(error);
            }
            this.isLoading = false;
          }
        });
    },
    handleCreate() {
      if (this.fromMyConsole) {
        this.$router.push({ name: "MyConsoleRedeemCodeBundleCreate" });
      } else {
        this.$router.push({ name: "RedeemCodeBundleCreate" });
      }
    },
    formatDate(date) {
      return date ? format(new Date(date), "yyyy-MM-dd") : "無期限";
    },
    modify(item) {
      this.selectedBundle.id = item.id;
      this.selectedBundle.merchant_id = item.merchant_id;
      this.selectedBundle.name = item.name;
      this.selectedBundle.action = 1;
      this.selectedBundle.quantity = 0;
      this.selectedBundle.redeemcode_type = item.redeemcode_type;

      this.selectedBundle.title = item.name + " 數量調整";

      if (item.redeemcode_type === consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.FIXED) {
        this.selectedBundle.static_redeem_code = item.static_redeem_code;
        this.selectedBundle.limit = item.static_redeem_code_ceil_limit;

      } else if (item.redeemcode_type === consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.RANDOM) {
        this.selectedBundle.limit                     = item.random_redeem_code_ceil_limit;
        this.selectedBundle.random_redeem_code_length = item.random_redeem_code_length;

      } else if ( item.redeemcode_type === consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.NONE) {
        this.selectedBundle.limit = item.none_redeem_code_ceil_limit;

      }

      this.$bvModal.show("modify-modal");
    },
    async submit() {
      if (this.resultQuantity < 0) {
        this.$swal.fire({
          type: "error",
          title: "調整後數量不得小於0",
          confirmButtonText: "確定",
          confirmButtonColor: "#d33",
          reverseButtons: true,
        });
        return;
      }

      this.isLoading = true;

      const quantityStr = String(this.resultQuantity);

      if (
        this.selectedBundle.redeemcode_type ===
        consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.FIXED
      ) {
        this.selectedBundle.static_redeem_code_ceil_limit = quantityStr;
      } else if (
        this.selectedBundle.redeemcode_type ===
        consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.RANDOM
      ) {
        this.selectedBundle.random_redeem_code_ceil_limit = quantityStr;
      } else if (
        this.selectedBundle.redeemcode_type ===
        consts.REDEEM_CODE_BUNDLE_REDEEMCODE_TYPES.NONE
      ) {
        this.selectedBundle.none_redeem_code_ceil_limit = quantityStr;
      }

      try {
        let response = await redeemCodeBundleApi.updateRedeemCodeBundleConfig(
          this.selectedBundle.id,
          {
            ...this.selectedBundle,
          }
        );

        if (response.status === 200) {
          this.$swal.fire({
            icon: "success",
            title: "更新數量成功",
            confirmButtonText: "確定",
            confirmButtonColor: "#19d895",
            reverseButtons: true,
          });

          await this.fetchRedeemCodeBundles();
        } else {
          this.$swal.fire({
            icon: "error",
            title: "更新數量失敗",
            text: response.data.message || "未知錯誤",
            confirmButtonText: "確定",
            confirmButtonColor: "#d33",
            reverseButtons: true,
          });
          this.isLoading = false;
        }
      } catch (error) {
        // 捕捉 400、500 等錯誤
        this.$swal.fire({
          icon: "error",
          title: "更新數量失敗",
          text: error.response?.data?.message || "發生錯誤，請稍後再試",
          confirmButtonText: "確定",
          confirmButtonColor: "#d33",
          reverseButtons: true,
        });
      }

      this.$bvModal.hide("modify-modal");
      this.isLoading = false;
    },
    formatTakeUsageType(value) {
      const takeType = this.takeTypes.find(
        (item) => item.value === value.take_type
      );
      const usageType = this.usageTypes.find(
        (item) => item.value === value.usage_type
      );

      const takeTypeText = takeType ? takeType.text : value.take_type;
      const usageTypeText = usageType ? usageType.text : value.usage_type;
      return takeTypeText + "<br>" + usageTypeText;
    },
    formatUsageStatus(value) {
      let text = [];
      const now = Date.now(); // 取得當前時間的毫秒數
      // 領取日到期
      if (now > new Date(value.end_at).getTime()) {
        text.push("領取到期");
      }

      // 使用日到期
      if (
        value.dateline_type === consts.REDEEM_CODE_BUNDLE_DATELINE_TYPES.CUSTOM
      ) {
        if (now > new Date(value.used_end_at).getTime()) {
          text.push("使用到期");
        }
      } else if (
        value.dateline_type ===
        consts.REDEEM_CODE_BUNDLE_DATELINE_TYPES.SAME_AS_TAKE_TIME
      ) {
        if (now > new Date(value.end_at).getTime()) {
          text.push("使用到期");
        }
      } else {
        text.push("依領取區間推算");
      }

      // 已超過領取上限
      if (value.redeemcode_type == 2) {
        // 固定
        // static_redeem_code_ceil_limit
        if (value.static_redeem_code_ceil_limit === null) {
          text.push("不限制數量");
        } else if (value.apply_at_amount >= value.static_redeem_code_ceil_limit) {
          text.push("已達數量上限");
        }
      } else if (value.redeemcode_type == 3) {
        // 隨機
        // random_redeem_code_ceil_limit
        if (value.random_redeem_code_ceil_limit === null) {
          text.push("不限制數量");
        } else if (value.apply_at_amount >= value.random_redeem_code_ceil_limit) {
          text.push("已達數量上限");
        }
      } else if (value.redeemcode_type == 0) {
        // 不使用
        // none_redeem_code_ceil_limit
        if (value.none_redeem_code_ceil_limit === null) {
          text.push("不限制數量");
        } else if (value.apply_at_amount >= value.none_redeem_code_ceil_limit) {
          text.push("已達數量上限");
        }
      }

      if (text.length > 0) {
        return text.join("<br>");
      } else {
        return "使用中";
      }
    },
    getBranchName(branch_id) {
      const option = this.branchOptions.find((option) =>
        branch_id === null
          ? option.value === "null"
          : option.value === branch_id
      );

      return option ? option.text : "";
    },
    reset() {
      this.query = { ...this.initQuery };
      this.handlePageReset();
    },
  },
};
</script>

<style>
.text-wrap {
  white-space: pre-line;
}
</style>
